import { Dropdown, Widget, Tabs, SortableWidgetProps } from '@creditsights/styleguide-web';
// eslint-disable-next-line import/no-unresolved
import { DealMarketStatus, getDealsTabs } from '@creditsights/cs-web';
import React, { forwardRef, ReactElement } from 'react';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';
import { COMPANY_ROUTES } from '../../../../Base.constants';
import useFetchCompanyDeals from '../../../../hooks/deals/useFetchCompanyDeals';
import useFetchUserPermissions from '../../../../hooks/permissions/useFetchUserPermissions';
import { DealSecurityType } from '../../../../models/CompanyDeal';
import { useCompanyDealsContext } from '../../../../pages/Company/CompanyDealsContext';
import { getCompanyId } from '../../../../utils/tagUtils';
import CompanyDealsPrintWidget from './CompanyDealsPrintWidget';
import CompanyDealsWidgetContent from './CompanyDealsWidgetContent';
import styles from './CompanyDealsWidget.module.scss';

const cx = classNames.bind(styles);

interface CompanyDealsWidgetProps extends SortableWidgetProps {
  companyTagId: string;
  print?: boolean;
}

const HeaderLink = ({ children }) => <Link to={COMPANY_ROUTES.deals}>{children}</Link>;

const DEFAULT_DEALS_COUNT = 5;

const dealSecurityTypes = [
  { text: 'Bonds', value: DealSecurityType.Bond },
  { text: 'Loans', value: DealSecurityType.Loan },
];

const dealsTabValues = {
  Expected: 'Expected',
  InMarket: 'Announced',
  Hung: ['Hung Syndication (Outstanding)', 'Hung Syndication'],
  Rumoured: 'Rumoured',
  Pulled: 'Pulled',
};

const calculateMarketStatuses = (
  dealSecurityType: DealSecurityType,
  activeTab: DealMarketStatus
): string[] | undefined => {
  if (activeTab === DealMarketStatus.InMarket) {
    return [dealsTabValues.InMarket];
  }
  if (activeTab === DealMarketStatus.Expected) {
    return dealSecurityType === DealSecurityType.Bond ? [dealsTabValues.Rumoured] : [dealsTabValues.Expected];
  }
  if (activeTab === DealMarketStatus.Completed) {
    const loansCompletedTab = [dealsTabValues.InMarket, dealsTabValues.Expected, ...dealsTabValues.Hung];
    const bondsCompletedTab = [dealsTabValues.InMarket, dealsTabValues.Rumoured, dealsTabValues.Pulled];

    return dealSecurityType === DealSecurityType.Bond ? bondsCompletedTab : loansCompletedTab;
  }
  if (activeTab === DealMarketStatus.Hung) {
    return dealSecurityType === DealSecurityType.Bond ? [dealsTabValues.Pulled] : [...dealsTabValues.Hung];
  }

  return undefined;
};

const getTabs = (
  tabs: DealMarketStatus[],
  activeTab: DealMarketStatus,
  setActiveTab: (tab: DealMarketStatus) => void
) => {
  return tabs.map((key) => {
    return {
      active: activeTab === key,
      label: key,
      onClick: () => setActiveTab(key),
    };
  });
};

// TODO [TECH-DEBT] CSNP-2065. This component should be moved to cs-web MFE
const CompanyDealsWidget = (
  { companyTagId, print, ...sortableWidgetProps }: CompanyDealsWidgetProps,
  ref
): ReactElement | null => {
  const companyId = getCompanyId(companyTagId);
  const { dealSecurityType, setDealSecurityType, activeTab, setActiveTab } = useCompanyDealsContext();
  const { data: userPermissions } = useFetchUserPermissions();
  const deals = useFetchCompanyDeals(companyId, {
    securityTypes: [dealSecurityType],
    marketStatuses: calculateMarketStatuses(dealSecurityType, activeTab),
    take: DEFAULT_DEALS_COUNT,
    sort: 'launch_date',
    enabled: true,
    excludeMarketStatuses: activeTab === DealMarketStatus.Completed,
  });

  if (Number.isNaN(Number(companyId))) {
    return null;
  }
  const tabConfig = getDealsTabs(userPermissions!);
  const tabs = getTabs(tabConfig, activeTab, setActiveTab);

  const renderSecondLine = () => {
    return (
      <div className={cx('tabs-container')}>
        {print ? (
          <span>{dealSecurityTypes.find((t) => t.value === dealSecurityType)?.text || dealSecurityType}</span>
        ) : (
          <Dropdown
            value={dealSecurityType}
            options={dealSecurityTypes}
            onOptionSelect={(value) => {
              setDealSecurityType(value as DealSecurityType);
            }}
            withCaretIcon
            withCheckmarks
          />
        )}
        <Tabs tabs={tabs} dataTestId="company-deals-tabs" />
      </div>
    );
  };

  if (print) {
    return <CompanyDealsPrintWidget secondLine={renderSecondLine()} deals={deals.data || []} />;
  }

  return (
    <div className={cx('body')} data-testid="company-deals-widget">
      <Widget
        headerTitle="Deals Pipeline"
        headerLink={HeaderLink}
        secondLine={renderSecondLine()}
        isLoading={deals.isLoading}
        hideSecondLineWhenLoading={false}
        ref={ref}
        {...sortableWidgetProps}
      >
        <CompanyDealsWidgetContent deals={deals.data || []} />
      </Widget>
    </div>
  );
};

export default forwardRef(CompanyDealsWidget);
